import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  InputAdornment,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  LocalOffer as OfferIcon,
  Palette as PaletteIcon,
  LocalOffer,
  Money,
} from "@mui/icons-material";
import { PropertyListing } from "../../../types/Property";
import {
  CreatePropertyPromotion,
  UpdatePropertyPromotion,
  PropertyPromotion,
} from "../../../types/PropertyPromotion";
import { PropertyPromotionService } from "../../../services/PropertyPromotionService";

interface PropertyOfferProps {
  selectedProperty: PropertyListing | null;
  onPromotionUpdate?: (promotion: PropertyPromotion | null) => void;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
}

export const PropertyOfferPage = ({
  selectedProperty,
  onPromotionUpdate,
}: PropertyOfferProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const propertyPromotionService = new PropertyPromotionService();

  // Estados para modales
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showPriceField, setShowPriceField] = useState(false);
  const [showTitleField, setShowTitleField] = useState(false);

  // Estados para formularios
  const [createForm, setCreateForm] = useState<CreatePropertyPromotion>({
    propertyId: selectedProperty?.id_Property || 0,
    title: "",
    newPrice: "",
    colorText: "#ffffff",
    colorTag: "#4caf50",
  });

  const [editForm, setEditForm] = useState<UpdatePropertyPromotion>({});

  // Estados para UI
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (
    message: string,
    severity: SnackbarState["severity"]
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreateSubmit = async () => {

    setLoading(true);
    try {
      if (
        createForm.newPrice &&
        isNaN(Number(createForm.newPrice))
      ) {
        showSnackbar(
          "El nuevo precio debe ser un número válido",
          "error"
        );
        return;
      }

      const result = await propertyPromotionService.create(createForm);
      if (result?.success) {
        showSnackbar(result.message || "Oferta creada exitosamente", "success");
        setCreateModalOpen(false);
        setCreateForm({
          propertyId: selectedProperty?.id_Property || 0,
          title: "",
          newPrice: "",
          colorText: "#ffffff",
          colorTag: "#4caf50",
        });
        if (onPromotionUpdate) onPromotionUpdate(result?.data);
        setShowPriceField(false);
      } else {
        showSnackbar(result?.message || "Error al crear la oferta", "error");
      }
    } catch (error: any) {
      showSnackbar(error.response.data.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedProperty?.promotion?.id) return;

    setLoading(true);
    try {
      const result = await propertyPromotionService.update(
        selectedProperty.promotion.id,
        editForm
      );
      if (result?.success) {
        showSnackbar(
          result.message || "Oferta actualizada exitosamente",
          "success"
        );
        setEditModalOpen(false);
        setEditForm({});
        if (onPromotionUpdate) onPromotionUpdate(result?.data);
        setShowPriceField(false);
      } else {
        showSnackbar(
          result?.message || "Error al actualizar la oferta",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProperty?.promotion?.id) return;

    setLoading(true);
    try {
      const result = await propertyPromotionService.delete(
        selectedProperty.promotion.id
      );
      if (result?.success) {
        showSnackbar(
          result.message || "Oferta eliminada exitosamente",
          "success"
        );
        setDeleteDialogOpen(false);
        if (onPromotionUpdate) onPromotionUpdate(null);
      } else {
        showSnackbar(result?.message || "Error al eliminar la oferta", "error");
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    if (selectedProperty?.promotion) {
      setEditForm({
        title: selectedProperty.promotion.title,
        newPrice: selectedProperty.promotion.newPrice,
        colorText: selectedProperty.promotion.colorText,
        colorTag: selectedProperty.promotion.colorTag,
      });
      setEditModalOpen(true);
    }
  };

  const DiscountPercentage = Math.round(
    ((selectedProperty!.price - parseFloat(createForm.newPrice)) /
      selectedProperty!.price) *
    100
  );

  const DiscountedPrice = Math.round(
    ((selectedProperty!.promotion?.oldPrice! - editForm.newPrice!) /
      selectedProperty!.promotion?.oldPrice!) *
    100
  );

  if (!selectedProperty) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          background: "#fff",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Selecciona una propiedad para gestionar ofertas
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "#fff",
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: "lg", margin: "0 auto" }}>
        {/* Header */}
        <Box sx={{ mb: 3, textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: "bold",
              background: "linear-gradient(45deg, #2e7d32, #4caf50)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: isMobile ? "18px" : "25px",
              mb: 1,
            }}
          >
            Gestión de Ofertas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedProperty.title}
          </Typography>
        </Box>

        {/* Contenido Principal */}
        {selectedProperty.promotion ? (
          // Mostrar oferta existente
          <Card
            elevation={6}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(76, 175, 80, 0.2)",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <OfferIcon
                    sx={{ color: selectedProperty.promotion.colorTag }}
                  />
                  {selectedProperty.promotion.discountPercentage > 0 && (
                    <Chip
                      label={`-${selectedProperty.promotion.discountPercentage}%`}
                      size="small"
                      sx={{
                        backgroundColor: selectedProperty.promotion.colorTag,
                        color: selectedProperty.promotion.colorText,
                        fontWeight: "bold",
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={openEditModal}
                    sx={{
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                      "&:hover": { backgroundColor: "rgba(76, 175, 80, 0.2)" },
                    }}
                  >
                    <EditIcon fontSize="small" sx={{ color: "#4caf50" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{
                      backgroundColor: "rgba(244, 67, 54, 0.1)",
                      "&:hover": { backgroundColor: "rgba(244, 67, 54, 0.2)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" sx={{ color: "#f44336" }} />
                  </IconButton>
                </Box>
              </Box>

              {selectedProperty.promotion.title && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: selectedProperty.promotion.colorTag,
                  mb: 2,
                  fontSize: { xs: "1.1rem", md: "1.25rem" },
                }}
              >
                {selectedProperty.promotion.title}
              </Typography>
              )}

              {selectedProperty.promotion.discountPercentage > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "rgba(76, 175, 80, 0.05)",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Precio Original
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        textDecoration: "line-through",
                        color: "text.secondary",
                        fontSize: { xs: "1rem", md: "1.25rem" },
                      }}
                    >
                      ₡
                      {selectedProperty.promotion.oldPrice.toLocaleString(
                        "es-CR"
                      )}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "rgba(76, 175, 80, 0.1)",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Precio de Oferta
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "#4caf50",
                        fontSize: { xs: "1rem", md: "1.25rem" },
                      }}
                    >
                      ₡
                      {selectedProperty.promotion.newPrice.toLocaleString(
                        "es-CR"
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              )}
              <Box
                sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(0,0,0,0.1)" }}
              >
                <Typography variant="body2" color="text.secondary">
                  Creada el:{" "}
                  {new Date(
                    selectedProperty.promotion.createdAt
                  ).toLocaleDateString("es-CR")}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          // Mostrar botón para crear oferta
          <Card
            elevation={4}
            sx={{
              borderRadius: 3,
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "2px dashed rgba(76, 175, 80, 0.3)",
            }}
          >
            <CardContent sx={{ textAlign: "center", p: { xs: 3, md: 4 } }}>
              <OfferIcon
                sx={{ fontSize: 60, color: "rgba(76, 175, 80, 0.3)", mb: 2 }}
              />
              <Typography variant="h6" sx={{ mb: 1, color: "text.secondary" }}>
                No hay ofertas activas
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                Crea una oferta especial para esta propiedad
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
                sx={{
                  background: "linear-gradient(45deg, #4caf50, #66bb6a)",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  "&:hover": {
                    background: "linear-gradient(45deg, #388e3c, #4caf50)",
                  },
                }}
              >
                Crear Oferta
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Modal Crear Oferta */}
        <Dialog
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              height: isMobile ? "100vh" : "auto",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(45deg, #4caf50, #66bb6a)",
              color: "white",
              position: isMobile ? "sticky" : "static",
              top: 0,
              zIndex: 1,
            }}
          >
            Crear Nueva Oferta
            <IconButton
              onClick={() => setCreateModalOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: { xs: 2, md: 3 },
              flex: isMobile ? 1 : "none",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                mt: 2,
                p: 1.5,
                backgroundColor: 'rgba(76, 175, 80, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}
            >
              <Money sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
              <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                Incluir título a la oferta
              </Typography>
              <Switch
                checked={showTitleField}
                onChange={(e) => {
                  setShowTitleField(e.target.checked);
                  if (!e.target.checked) {
                    setCreateForm({ ...createForm, title: '' });
                  }
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4caf50',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                }}
              />
            </Box>
            {showTitleField && (
              <TextField
                fullWidth
                label="Título de la oferta"
                value={createForm.title}
                onChange={(e) =>
                setCreateForm({ ...createForm, title: e.target.value })
              }
              sx={{ mb: 2 }}
              size={isMobile ? "medium" : "small"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOffer sx={{ color: "#4caf50" }} />
                  </InputAdornment>
                ),
              }}
            />
            )}

            {/* Switch para mostrar/ocultar campo de precio */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                p: 1.5,
                backgroundColor: 'rgba(76, 175, 80, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}
            >
              <Money sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
              <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                Incluir nuevo precio de descuento
              </Typography>
              <Switch
                checked={showPriceField}
                onChange={(e) => {
                  setShowPriceField(e.target.checked);
                  if (!e.target.checked) {
                    setCreateForm({ ...createForm, newPrice: '' });
                  }
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4caf50',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                }}
              />
            </Box>

            {/* Campo de precio condicional */}
            {showPriceField && (
              <TextField
                fullWidth
                label="Nuevo Precio"
                type="number"
                value={createForm.newPrice}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    newPrice: e.target.value,
                  })
                }
                sx={{ mb: 3 }}
                size={isMobile ? "medium" : "small"}
                inputProps={{ min: 1, max: 99 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Money sx={{ color: "#4caf50" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <Grid container spacing={isMobile ? 3 : 2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PaletteIcon fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                      Color del texto
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <input
                        type="color"
                        value={createForm.colorText}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            colorText: e.target.value,
                          })
                        }
                        style={{
                          width: isMobile ? "50px" : "40px",
                          height: isMobile ? "50px" : "40px",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        Seleccionar
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PaletteIcon fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                      Color del tag
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <input
                        type="color"
                        value={createForm.colorTag}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            colorTag: e.target.value,
                          })
                        }
                        style={{
                          width: isMobile ? "50px" : "40px",
                          height: isMobile ? "50px" : "40px",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        Seleccionar
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Preview */}
            <Box
              sx={{
                mt: 3,
                p: { xs: 2.5, md: 2 },
                backgroundColor: "rgba(0,0,0,0.02)",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ mb: 2 }} fontWeight="medium">
                Vista previa:
              </Typography>
              <Chip
                label={
                  showPriceField && createForm.newPrice
                    ? `-${DiscountPercentage}%`
                    : createForm.title.toUpperCase() || 'Título de la oferta'
                }
                size={isMobile ? "medium" : "small"}
                sx={{
                  backgroundColor: createForm.colorTag,
                  color: createForm.colorText,
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.875rem" : "0.75rem",
                  height: isMobile ? 36 : 24,
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: { xs: 2, md: 2 },
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              position: isMobile ? "sticky" : "static",
              bottom: 0,
              backgroundColor: "white",
              borderTop: isMobile ? "1px solid rgba(0,0,0,0.1)" : "none",
            }}
          >
            <Button
              onClick={() => setCreateModalOpen(false)}
              disabled={loading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSubmit}
              disabled={loading || (showPriceField && !createForm.newPrice) || (!showPriceField && !showTitleField) || (showTitleField && !createForm.title.trim())}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                background: "linear-gradient(45deg, #4caf50, #66bb6a)",
                "&:hover": {
                  background: "linear-gradient(45deg, #388e3c, #4caf50)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Crear Oferta"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal Editar Oferta */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              height: isMobile ? "100vh" : "auto",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(45deg, #4caf50, #66bb6a)",
              color: "white",
              position: isMobile ? "sticky" : "static",
              top: 0,
              zIndex: 1,
            }}
          >
            Editar Oferta
            <IconButton
              onClick={() => setEditModalOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: { xs: 2, md: 3 },
              flex: isMobile ? 1 : "none",
              overflowY: "auto",
            }}
          >
          <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                mt: 2,
                p: 1.5,
                backgroundColor: 'rgba(76, 175, 80, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}
            >
              <Money sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
              <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                Incluir título a la oferta
              </Typography>
              <Switch
                checked={showTitleField}
                onChange={(e) => {
                  setShowTitleField(e.target.checked);
                  if (!e.target.checked) {
                    setCreateForm({ ...createForm, title: '' });
                  }
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4caf50',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                }}
              />
            </Box>
            {showTitleField && (
            <TextField
              fullWidth
              label="Título de la oferta"
              value={editForm.title || ""}
              onChange={(e) =>
                setEditForm({ ...editForm, title: e.target.value })
              }
              sx={{ mb: 2, mt: 2 }}
              size={isMobile ? "medium" : "small"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOffer sx={{ color: "#4caf50" }} />
                  </InputAdornment>
                ),
              }}
            />
            )}

             <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                p: 1.5,
                backgroundColor: 'rgba(76, 175, 80, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}
            >
              <Money sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
              <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                Incluir nuevo precio de descuento
              </Typography>
              <Switch
                checked={showPriceField}
                onChange={(e) => {
                  setShowPriceField(e.target.checked);
                  if (!e.target.checked) {
                    setCreateForm({ ...createForm, newPrice: '' });
                  }
                }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4caf50',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                }}
              />
            </Box>

            {/* Campo de precio condicional */}
            {showPriceField && (
            <TextField
              fullWidth
              label="Nuevo Precio"
              type="number"
              value={editForm.newPrice || ""}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  newPrice: Number(e.target.value),
                })
              }
              sx={{ mb: 3 }}
              size={isMobile ? "medium" : "small"}
              inputProps={{ min: 1, max: 99 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Money sx={{ color: "#4caf50" }} />
                  </InputAdornment>
                ),
              }}
            />
            )}
            <Grid container spacing={isMobile ? 3 : 2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PaletteIcon fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                      Color del texto
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <input
                        type="color"
                        value={editForm.colorText || "#ffffff"}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            colorText: e.target.value,
                          })
                        }
                        style={{
                          width: isMobile ? "50px" : "40px",
                          height: isMobile ? "50px" : "40px",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        Seleccionar
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <PaletteIcon fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                      Color del tag
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <input
                        type="color"
                        value={editForm.colorTag || "#4caf50"}
                        onChange={(e) =>
                          setEditForm({ ...editForm, colorTag: e.target.value })
                        }
                        style={{
                          width: isMobile ? "50px" : "40px",
                          height: isMobile ? "50px" : "40px",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        Seleccionar
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Preview */}
            <Box
              sx={{
                mt: 3,
                p: { xs: 2.5, md: 2 },
                backgroundColor: "rgba(0,0,0,0.02)",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" sx={{ mb: 2 }} fontWeight="medium">
                Vista previa:
              </Typography>
              <Chip
                label={
                  showPriceField && editForm.newPrice
                    ? `-${DiscountedPrice}%`
                    : editForm.title!.toUpperCase() || 'Título de la oferta'
                }
                size={isMobile ? "medium" : "small"}
                sx={{
                  backgroundColor: editForm.colorTag,
                  color: editForm.colorText,
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.875rem" : "0.75rem",
                  height: isMobile ? 36 : 24,
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: { xs: 2, md: 2 },
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              position: isMobile ? "sticky" : "static",
              bottom: 0,
              backgroundColor: "white",
              borderTop: isMobile ? "1px solid rgba(0,0,0,0.1)" : "none",
            }}
          >
            <Button
              onClick={() => setEditModalOpen(false)}
              disabled={loading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSubmit}
              disabled={loading}
              fullWidth={isMobile}
              size={isMobile ? "large" : "medium"}
              sx={{
                background: "linear-gradient(45deg, #4caf50, #66bb6a)",
                "&:hover": {
                  background: "linear-gradient(45deg, #388e3c, #4caf50)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Actualizar"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Confirmación Eliminar */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirmar Eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Estás seguro de que deseas eliminar esta oferta? Esta acción no
              se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Eliminar"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para notificaciones */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Loading Backdrop */}
        <Backdrop open={loading} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Box>
  );
};
